import { useState, useEffect } from 'react';
import { useAuthStore, api } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Settings as SettingsIcon, 
  Mail, 
  MessageSquare, 
  Phone,
  Loader2,
  Save,
  Database,
  RefreshCw,
  Server,
  HardDrive,
  Users,
  Building2,
  Calendar,
  FileText,
  Shield
} from 'lucide-react';

const SETTINGS_GROUPS = [
  {
    id: 'email',
    icon: Mail,
    title: 'Email (SMTP)',
    keys: ['smtp_host', 'smtp_port', 'smtp_user', 'smtp_pass', 'smtp_from']
  },
  {
    id: 'sms',
    icon: Phone,
    title: 'SMS',
    keys: ['sms_provider_url', 'sms_api_key']
  },
  {
    id: 'whatsapp',
    icon: MessageSquare,
    title: 'WhatsApp',
    keys: ['whatsapp_provider_url', 'whatsapp_api_key']
  }
];

const SETTING_LABELS = {
  smtp_host: 'SMTP Host',
  smtp_port: 'SMTP Port',
  smtp_user: 'SMTP Username',
  smtp_pass: 'SMTP Password',
  smtp_from: 'From Email',
  sms_provider_url: 'Provider URL',
  sms_api_key: 'API Key',
  whatsapp_provider_url: 'Provider URL',
  whatsapp_api_key: 'API Key'
};

export const Settings = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  
  // Database settings
  const [dbInfo, setDbInfo] = useState(null);
  const [dbLoading, setDbLoading] = useState(false);
  const [dbForm, setDbForm] = useState({ mongo_url: '', db_name: '' });
  const [dbSaving, setDbSaving] = useState(false);

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  useEffect(() => {
    fetchSettings();
    if (isSuperAdmin) {
      fetchDatabaseInfo();
    }
  }, [isSuperAdmin]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/settings/');
      if (response.data.success) {
        const settingsMap = {};
        response.data.data.forEach(s => {
          settingsMap[s.key] = s.value;
        });
        setSettings(settingsMap);
      }
    } catch (error) {
      console.error('Failed to fetch settings', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDatabaseInfo = async () => {
    setDbLoading(true);
    try {
      const response = await api.get('/settings/database');
      if (response.data.success) {
        setDbInfo(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch database info', error);
    } finally {
      setDbLoading(false);
    }
  };

  const handleSaveDatabase = async () => {
    if (!dbForm.mongo_url && !dbForm.db_name) {
      toast.error('En az bir alan doldurun');
      return;
    }
    
    setDbSaving(true);
    try {
      const response = await api.put('/settings/database', dbForm);
      if (response.data.success) {
        toast.success('Veritabanı ayarları güncellendi. Sunucu yeniden başlatılmalı!');
        setDbForm({ mongo_url: '', db_name: '' });
        fetchDatabaseInfo();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncelleme başarısız');
    } finally {
      setDbSaving(false);
    }
  };

  const handleSave = async (key) => {
    setSaving({ ...saving, [key]: true });
    try {
      await api.put('/settings/', { key, value: settings[key] || '' });
      toast.success(`${SETTING_LABELS[key]} saved`);
    } catch (error) {
      toast.error('Failed to save setting');
    } finally {
      setSaving({ ...saving, [key]: false });
    }
  };

  const handleChange = (key, value) => {
    setSettings({ ...settings, [key]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <div>
        <h1 
          className="text-2xl font-bold text-slate-900 dark:text-slate-100"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          {t('common.settings')}
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          {user?.role === 'SUPER_ADMIN' ? 'Global' : 'Firm'} notification settings
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
        <p className="text-amber-800 text-sm">
          <strong>Note:</strong> These settings are placeholders for future notification integration. 
          Values are stored but not yet used for sending notifications.
        </p>
      </div>

      {/* Database Settings - Super Admin Only */}
      {isSuperAdmin && (
        <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-6" data-testid="database-settings">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Database className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Veritabanı Ayarları
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400">MongoDB bağlantı bilgileri</p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={fetchDatabaseInfo}
              disabled={dbLoading}
            >
              <RefreshCw className={`h-4 w-4 ${dbLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {dbLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : dbInfo ? (
            <div className="space-y-6">
              {/* Connection Info */}
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Server className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                  <span className="font-medium text-slate-700 dark:text-slate-200">Bağlantı Bilgileri</span>
                  <span className="ml-auto px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                    {dbInfo.connection.status}
                  </span>
                </div>
                <div className="grid gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-400">URL:</span>
                    <code className="text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">{dbInfo.connection.url}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-400">Database:</span>
                    <code className="text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded font-bold">{dbInfo.connection.db_name}</code>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <HardDrive className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                  <span className="font-medium text-slate-700 dark:text-slate-200">Veritabanı İstatistikleri</span>
                  <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
                    Toplam: {dbInfo.total_documents.toLocaleString()} kayıt
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-white dark:bg-slate-800 rounded-lg p-3 text-center">
                    <Users className="h-5 w-5 mx-auto text-blue-500 mb-1" />
                    <div className="text-lg font-bold text-slate-800 dark:text-slate-100">{dbInfo.stats.users}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Kullanıcı</div>
                  </div>
                  <div className="bg-white dark:bg-slate-800 rounded-lg p-3 text-center">
                    <Building2 className="h-5 w-5 mx-auto text-purple-500 mb-1" />
                    <div className="text-lg font-bold text-slate-800 dark:text-slate-100">{dbInfo.stats.firms}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Firma</div>
                  </div>
                  <div className="bg-white dark:bg-slate-800 rounded-lg p-3 text-center">
                    <Calendar className="h-5 w-5 mx-auto text-emerald-500 mb-1" />
                    <div className="text-lg font-bold text-slate-800 dark:text-slate-100">{dbInfo.stats.leave_requests}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">İzin Talebi</div>
                  </div>
                  <div className="bg-white dark:bg-slate-800 rounded-lg p-3 text-center">
                    <Shield className="h-5 w-5 mx-auto text-amber-500 mb-1" />
                    <div className="text-lg font-bold text-slate-800 dark:text-slate-100">{dbInfo.stats.audit_logs}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Audit Log</div>
                  </div>
                </div>
              </div>

              {/* Collections */}
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                  <span className="font-medium text-slate-700 dark:text-slate-200">Collections ({dbInfo.collections.length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {dbInfo.collections.map(col => (
                    <span key={col} className="px-2 py-1 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs rounded border">
                      {col}
                    </span>
                  ))}
                </div>
              </div>

              {/* Update Form */}
              <div className="border-t pt-4">
                <h3 className="font-medium text-slate-700 dark:text-slate-200 mb-3">Bağlantı Ayarlarını Güncelle</h3>
                <div className="bg-red-50 border border-red-100 rounded-lg p-3 mb-4">
                  <p className="text-red-700 text-xs">
                    ⚠️ Dikkat: Bu ayarları değiştirmek sunucu yeniden başlatılmasını gerektirir. 
                    Yanlış değer girerseniz sistem çalışmayabilir.
                  </p>
                </div>
                <div className="grid gap-4">
                  <div>
                    <Label className="text-sm text-slate-600 dark:text-slate-300">MongoDB URL</Label>
                    <Input
                      type="password"
                      placeholder="mongodb://user:pass@host:27017"
                      value={dbForm.mongo_url}
                      onChange={(e) => setDbForm({ ...dbForm, mongo_url: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600 dark:text-slate-300">Database Adı</Label>
                    <Input
                      placeholder="hrportal"
                      value={dbForm.db_name}
                      onChange={(e) => setDbForm({ ...dbForm, db_name: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <Button 
                    onClick={handleSaveDatabase} 
                    disabled={dbSaving || (!dbForm.mongo_url && !dbForm.db_name)}
                    className="w-full sm:w-auto"
                  >
                    {dbSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Kaydet
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-slate-500 dark:text-slate-400 text-center py-4">Veritabanı bilgileri yüklenemedi</p>
          )}
        </div>
      )}

      <div className="space-y-6">
        {SETTINGS_GROUPS.map((group) => (
          <div 
            key={group.id} 
            className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-6"
            data-testid={`settings-group-${group.id}`}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
                <group.icon className="h-5 w-5 text-slate-600 dark:text-slate-300" />
              </div>
              <h2 
                className="text-lg font-semibold text-slate-900 dark:text-slate-100"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {group.title}
              </h2>
            </div>

            <div className="grid gap-4">
              {group.keys.map((key) => (
                <div key={key} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                  <Label className="w-32 text-slate-600 dark:text-slate-300 text-sm">
                    {SETTING_LABELS[key]}
                  </Label>
                  <div className="flex-1 flex gap-2">
                    <Input
                      type={key.includes('pass') || key.includes('key') ? 'password' : 'text'}
                      value={settings[key] || ''}
                      onChange={(e) => handleChange(key, e.target.value)}
                      placeholder={`Enter ${SETTING_LABELS[key].toLowerCase()}`}
                      className="flex-1"
                      data-testid={`setting-input-${key}`}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSave(key)}
                      disabled={saving[key]}
                      data-testid={`save-setting-${key}`}
                    >
                      {saving[key] ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Settings;
